import { Stepper } from "@shared/components/Stepper";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useCallback, useMemo, useState } from "react";
import ApprovalStep from "./steps/ApprovalStep";
import BillCycleStep from "./steps/BillCycleStep";
import BillGenerationStep from "./steps/BillGenerationStep";
import BillingSummaryStep from "./steps/BillingSummaryStep";
import FetchConsumerStep from "./steps/FetchConsumerStep";
import MeterReadingsStep from "./steps/MeterReadingsStep";

export const BillingWorkflow = () => {
  // State for cross-step data sharing (like selectedMeters)
  const [selectedMeters, setSelectedMeters] = useState<string[]>([]);

  // Storage key for this workflow
  const storageKey = `billing-workflow-progress-${getRemoteUtilityId()}`;

  // Handler for meter selection change (shared between steps)
  const handleMeterSelectionChange = useCallback((selectedRows: any[]) => {
    setSelectedMeters(selectedRows);
  }, []);

  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Bill Cycle",
        slug: "bill-cycle",
        component: <BillCycleStep />,
        optional: false,
        infoTitle: "Bill Cycle Help",
        infoDescription:
          "For a consumer’s first bill, the activation date must fall between the invoice period start date and end date. Set up the billing cycle parameters including start and end dates, billing frequency, and other cycle-specific settings.",
      },
      {
        title: "Fetch Consumer Data",
        slug: "fetch-consumer",
        component: <FetchConsumerStep />,
        optional: false,
        infoTitle: "Fetch Consumer Data Help",
        infoDescription:
          "This step retrieves all consumer data required for billing. The system will fetch customer details, account information, and service configurations.",
      },
      {
        title: "Meter Readings",
        slug: "meter-readings",
        component: (
          <MeterReadingsStep
            onSelectionChange={handleMeterSelectionChange}
            // selectedMeters={selectedMeters}
          />
        ),
        optional: false,
        infoTitle: "Meter Readings Help",
        infoDescription:
          "Review meter readings and select which meters to include in the billing process. You can filter readings by date, meter type, or consumer group.",
      },
      {
        title: "Bill Generation",
        slug: "bill-generation",
        component: <BillGenerationStep selectedMeters={selectedMeters} />,
        optional: false,
        infoTitle: "Bill Generation Help",
        infoDescription:
          "Generate bills for the selected meters. This process calculates charges, applies tariffs, and creates the final bill documents.",
      },
      {
        title: "Billing Summary",
        slug: "billing-summary",
        component: <BillingSummaryStep />,
        optional: false,
        infoTitle: "Billing Summary Help",
        infoDescription:
          "Review the complete billing summary including total amounts, number of bills generated, and any exceptions or errors that occurred.",
      },
      {
        title: "Approve",
        slug: "approve",
        component: <ApprovalStep />,
        optional: false,
        infoTitle: "Approval Help",
        infoDescription:
          "Final step to review and approve the billing run. Once approved, bills will be finalized and ready for distribution.",
      },
    ],
    [handleMeterSelectionChange, selectedMeters]
  );

  // Handle step completion
  const handleStepSave = useCallback(
    async (stepIndex: number, stepData: any, isDirty: boolean) => {
      console.log(`Saving step ${stepIndex}:`, stepData);

      return Promise.resolve();
    },
    []
  );

  // Handle workflow completion
  const handleComplete = useCallback(
    (allStepData: any, completedSteps: number[], skippedSteps: number[]) => {
      console.log("Billing Workflow completed!", {
        allStepData,
        completedSteps,
        skippedSteps,
        selectedMeters,
      });
    },
    [selectedMeters]
  );

  const handleStepChange = useCallback(
    (stepIndex: number, previousStepIndex: number, stepInfo: any) => {
      console.log(
        `Step changed from ${previousStepIndex} to ${stepIndex}`,
        stepInfo
      );
    },
    []
  );

  // Validation handler
  const handleValidation = useCallback(
    async (stepIndex: number, stepData: any) => {
      // Add your validation logic here
      console.log(`Validating step ${stepIndex}:`, stepData);

      // Return validation result
      return { isValid: true };
    },
    []
  );

  return (
    <div className="">
      <div className="mb-4">
        <h1 className="page-title">
          Billing Workflow
        </h1>
        <p className="page-subtitle">
          Create and manage billing cycles using this streamlined workflow
        </p>
      </div>

      <Stepper
        steps={steps}
        onStepChange={handleStepChange}
        onStepSave={handleStepSave}
        enableApiSave={false}
        saveOnStepChange={false}
        trackDirtyState={true}
        autoSave={true}
        persistProgress={true}
        storageKey={storageKey}
        enableRouting={true}
        basePath="/bx/create-bill-generation"
        onValidation={handleValidation}
        onComplete={handleComplete}
        allowStepClick={false}
        className="mb-8"
      />
    </div>
  );
};
